import Hls from "hls.js";
import { isHlsUrl } from "./mediaPlayUrl";

export type AttachHlsResult = {
  hls: Hls | null;
  viaHlsJs: boolean;
  mode: "hls.js" | "native" | "progressive";
};

export type AttachHlsCallbacks = {
  onLog?: (message: string) => void;
  onFatal?: (summary: string) => void;
  onManifestParsed?: (info: {
    levels: number;
    audioTracks: number;
  }) => void;
};

/**
 * Prefer hls.js whenever MSE is available.
 * Native HLS only when hls.js is NOT supported (Safari / iOS).
 */
export function attachHlsToVideo(
  video: HTMLVideoElement,
  src: string,
  options?: Partial<Hls["config"]>,
  callbacks?: AttachHlsCallbacks,
): AttachHlsResult {
  const log = callbacks?.onLog || (() => {});

  if (!src) {
    log("Пустой источник");
    return { hls: null, viaHlsJs: false, mode: "progressive" };
  }

  if (!isHlsUrl(src)) {
    video.src = src;
    return { hls: null, viaHlsJs: false, mode: "progressive" };
  }

  const mse = Hls.isSupported();

  if (mse) {
    const hls = new Hls({
      enableWorker: false,
      lowLatencyMode: false,
      backBufferLength: 30,
      ...options,
    });
    hls.loadSource(src);
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      try {
        hls.startLoad(-1);
      } catch {
        /* */
      }
    });

    hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
      log("HLS манифест получен");
      callbacks?.onManifestParsed?.({
        levels: data.levels?.length || 0,
        audioTracks: data.audioTracks?.length || 0,
      });
    });

    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data?.fatal) return;
      log("Ошибка HLS");
      callbacks?.onFatal?.(`${data.type}/${data.details}`);
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        try {
          hls.recoverMediaError();
        } catch {
          /* */
        }
      }
    });

    log("HLS инициализирован");
    return { hls, viaHlsJs: true, mode: "hls.js" };
  }

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    log("HLS native");
    return { hls: null, viaHlsJs: false, mode: "native" };
  }

  log("HLS не поддерживается");
  return { hls: null, viaHlsJs: false, mode: "progressive" };
}
